import * as isEmpty from 'lodash/isEmpty.js';
import { IComponentOptions, IFilterService } from 'angular';
import { INgRedux } from 'ng-redux';
import { getChartColors } from 'app/actions/theme.actions.js';
import api from 'dashboard/resources/my-net-sales-performance.api';
import { NetSales, NetSalesTranslation, SalesCategory, WeeklySales } from 'dashboard/resources/my-net-sales-performance.types';
import { IMyNetSalesPerformanceWidgetComponentController, SelectOption } from './my-net-sales-performance-widget.types';
import * as template from './my-net-sales-performance-widget.html';

const CATEGORY_ALL = 'All';
const THIS_YEAR = 'ThisYear';

const mapStateToThis = ({ session, theme, dictionary }) => {
  const { locale, retailer } = session;
  const terms = dictionary.terms[locale];

  return {
    locale,
    theme,
    retailer,
    terms,
  };
};

class MyNetSalesPerformanceWidgetController implements IMyNetSalesPerformanceWidgetComponentController {
  // prettier-ignore
  static $inject = [
    '$scope',
    '$ngRedux',
    '$state',
    '$filter',
    'gettextCatalog',
  ];

  public isEditMode: boolean;
  public isRemovable: boolean;
  public removeWidget: any;

  private unsubscribe: any;
  private isEmpty: boolean;
  private isLoading: boolean;
  private isMaintenance: boolean;
  private terms: any;
  private retailer: any;
  private retailerId: number;
  private locale: string;
  private data: NetSales[];
  private translations: NetSalesTranslation[];
  private allCategories: SalesCategory[];
  private categories: SelectOption[] = [];
  private categoryId: number;
  private dataTypes: SelectOption[] = [];
  private dataTypeId: number;
  private theme: any;
  private colors: any;
  private chartOptions: any;
  private chartData: any;

  // prettier-ignore
  constructor(
    protected $scope: any,
    protected $ngRedux: INgRedux,
    protected $state: any,
    protected $filter: IFilterService,
    protected gettextCatalog: any
  ) {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, null)(this);
  }

  $onInit() {
    this.retailerId = parseInt(this.$state.params.retailerNo, 10) || this.retailer.retailerId;
    this.colors = getChartColors(this.theme.colors);
    this.$scope.$applyAsync(
      this.fetchData().then(() => {
        this.setDataTypes();
        this.setChartOptions();
        this.setChartData();
      })
    );
  }

  $onDestroy() {
    this.unsubscribe();
  }

  setDataTypes() {
    if (!this.data) return;
    this.data.forEach((item: NetSales) => {
      if (item.name !== THIS_YEAR) {
        this.dataTypes.push({
          id: item.displayNumber,
          label: this.findTranslationValue(item.name),
        });
      }
    });
    this.dataTypeId = this.dataTypes.length > 0 ? this.dataTypes[0].id : null;
  }

  async fetchData() {
    try {
      this.isMaintenance = false;
      this.isEmpty = true;
      this.isLoading = true;
      await Promise.all([this.fetchMyNetSalesTranslations(), this.fetchAllCategories()]);
      await this.fetchNetSalesData();
    } catch (error) {
      this.isMaintenance = true;
      console.error('FETCH_DATA_MY_NET_SALES_PERFORMANCE_WIDGET', error);
    } finally {
      this.isLoading = false;
    }
  }

  async fetchMyNetSalesTranslations() {
    this.translations = await api.fetchMyNetSalesTranslations(this.locale);
  }

  findTranslationValue(name) {
    const { value } = this.translations.find((translation: NetSalesTranslation) => translation.name === name) || {};
    return value;
  }

  async fetchAllCategories() {
    this.allCategories = await api.fetchSalesCategories();
    this.setCategories();
  }

  setCategories() {
    const allCategory = this.allCategories.find((category: SalesCategory) => category.categoryType === CATEGORY_ALL);

    this.categories = [
      {
        id: 0, // allCategory.id,
        label: this.gettextCatalog.getString('Total {{ draw }} and {{ instant }} Game Sales', {
          instant: this.terms.INSTANT.singular,
          draw: this.terms.DRAW.singular,
        }),
      },
      {
        id: 1, // instantCategory.id,
        label: this.gettextCatalog.getString('{{ instant }} Game Sales', { instant: this.terms.INSTANT.singular }),
      },
      {
        id: 2, // drawCategory.id,
        label: this.gettextCatalog.getString('{{ draw }} Game Sales', { draw: this.terms.DRAW.singular }),
      },
    ];

    this.categoryId = allCategory ? allCategory.id : null;
  }

  async fetchNetSalesData() {
    this.data = await api.fetchMyNetSalesPerformanceData(this.retailerId, this.categoryId);
    this.isEmpty = isEmpty(this.data);
  }

  setChartData() {
    if (!this.data) return;
    const [color1, color2] = this.colors;
    this.chartData = [];
    const dataItemThisYear = this.data.find((item: NetSales) => item.name === THIS_YEAR);
    const dataItemComparison = this.data.find((item: NetSales) => item.displayNumber === this.dataTypeId);

    const getLineChartData = (color, netSales: NetSales, netSalesForComparison: NetSales) => {
      const compareWeeklySales = (weeklySales1: WeeklySales, weeklySales2: WeeklySales) =>{
        if(!weeklySales1?.sales && !weeklySales2?.sales)
          return 0;
        
        if(!weeklySales1?.sales)
          return -100;
        
        if(!weeklySales2?.sales)
          return 100;
        
        return (weeklySales1.sales - weeklySales2.sales) / weeklySales2.sales * 100;
      }
        

      return {
        color,
        key: this.findTranslationValue(netSales.name),
        values:
          netSales.weeklySales.length > 0
            ? netSales.weeklySales.map((weeklySales: WeeklySales) => {
                const weeklySalesComparison =
                  netSalesForComparison && netSalesForComparison.weeklySales.length > 0
                    ? netSalesForComparison.weeklySales.find(
                        (weeklySales2: WeeklySales) => weeklySales2.weekNumber === weeklySales.weekNumber
                      )
                    : null;

                return {
                  x: weeklySales.weekNumber,
                  y: weeklySales.sales,
                  weekEndDate: weeklySales.weekEndDate,
                  comparison: compareWeeklySales(weeklySales, weeklySalesComparison),
                };
              })
            : [],
      };
    };

    if (dataItemThisYear && dataItemComparison) {
      this.chartData = [
        getLineChartData(color1, dataItemThisYear, dataItemComparison),
        getLineChartData(color2, dataItemComparison, dataItemThisYear),
      ];
    } else if (dataItemThisYear && !dataItemComparison) {
      this.chartData = [getLineChartData(color1, dataItemThisYear, dataItemComparison)];
    }
  }

  setChartOptions() {
    const currencySymbol = undefined;
    const currencyFractionSize = 0;

    this.chartOptions = {
      chart: {
        clipEdge: false,
        type: 'lineChart',
        height: 280,
        margin: { left: 90 },
        showLabels: false,
        showLegend: false,
        growOnHover: false,
        tooltips: {
          enabled: true,
        },
        xAxis: {
          showMaxMin: false,
          axisLabel: this.gettextCatalog.getString('Week'),
        },
        yAxis: {
          showMaxMin: false,
          tickFormat: (tickVal) => {
            return this.$filter('currency')(tickVal, currencySymbol, currencyFractionSize);
          },
        },
        dispatch: {
          renderEnd() {
            // HACK: RWZ-10834
            setTimeout(() => {
              document.querySelectorAll('path.nv-point.hover').forEach((item) => item.classList.remove('hover'));
            }, 0);
          },
        },
        tooltip: {
          // eslint-disable-next-line consistent-return
          contentGenerator: (e) => {
            const { point } = e;
            const { color, y: value, weekEndDate, comparison } = point;
            return `
              <table>
                <thead>
                  <tr>
                    <td class="key">
                      <strong>${this.gettextCatalog.getString('Week ending')}</strong>
                    </td>
                    <td class="x-value">
                      <strong>${this.$filter('date')(weekEndDate, 'shortDate')}</strong>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="legend-color-guide">
                      <div style="background-color: ${color}"></div>
                    </td>
                    <td class="key">
                      <strong>${this.$filter('currency')(Math.round(value), currencySymbol, currencyFractionSize)}</strong>
                    </td>
                  </tr>
                  ${
                    comparison
                      ? `<tr class="${comparison >= 0 ? 'success' : 'danger'}">
                           <td><i class="fa ${comparison >= 0 ? 'fa-level-up' : 'fa-level-down'}"></i></td>
                           <td><strong>${comparison.toFixed(2)}%</strong></td>
                        </tr>`
                      : ''
                  }
                </tbody>
               </table>`;
          },
        },
      },
    };
  }

  async onChangeCategory() {
    await this.fetchNetSalesData();
    this.setChartData();
  }
}

export default class MyNetSalesPerformanceWidgetComponent implements IComponentOptions {
  public bindings: any;
  public template: any;
  public controller: any;

  constructor() {
    this.template = template;
    this.controller = MyNetSalesPerformanceWidgetController;
    this.bindings = {
      isEditMode: '<',
      isRemovable: '<',
      removeWidget: '&?',
    };
  }
}



// WEBPACK FOOTER //
// ./src/modules/dashboard/components/dashboard-widget/my-net-sales-performance/my-net-sales-performance-widget-component.ts